import { useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export function useProductos(sede, schemaName, sessionId) {
  const [departamentos, setDepartamentos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [tags, setTags] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [depSeleccionado, setDepSeleccionado] = useState(null);
  const [grupoSeleccionado, setGrupoSeleccionado] = useState(null);
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState(null);
  const [tagSeleccionado, setTagSeleccionado] = useState(null);
  const [marcaSeleccionada, setMarcaSeleccionada] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [busquedaInput, setBusquedaInput] = useState('');
  const [productos, setProductos] = useState([]);
  const [cargandoProductos, setCargandoProductos] = useState(false);
  const [cursorUltimo, setCursorUltimo] = useState(null);
  const cursorUltimaDescripcionRef = useRef(null);
  const [hasMore, setHasMore] = useState(false);
  const [cargandoMas, setCargandoMas] = useState(false);
  const searchTimeout = useRef(null);
  const requestIdRef = useRef(0);
  const sedeCodigoRef = useRef(null);
  const filtroAnteriorRef = useRef({ depSeleccionado: null, grupoSeleccionado: null, categoriaSeleccionada: null, tagSeleccionado: null, marcaSeleccionada: null, busqueda: '' });

  useEffect(() => {
    if (!schemaName) return;
    axios
      .get(`${API_URL}/catalogo/departamentos?schema_name=${schemaName}`)
      .then((res) => {
        if (res.data.status === 'success') {
          setDepartamentos(res.data.data || []);
        }
      })
      .catch(() => {});
    axios
      .get(`${API_URL}/catalogo/categorias?schema_name=${schemaName}`)
      .then((res) => {
        if (res.data.status === 'success') {
          setCategorias(res.data.data || []);
        }
      })
      .catch(() => {});
    axios
      .get(`${API_URL}/catalogo/tags?schema_name=${schemaName}`)
      .then((res) => {
        if (res.data.status === 'success') {
          setTags(res.data.data || []);
        }
      })
      .catch(() => {});
    axios
      .get(`${API_URL}/catalogo/marcas?schema_name=${schemaName}`)
      .then((res) => {
        if (res.data.status === 'success') {
          setMarcas(res.data.data || []);
        }
      })
      .catch(() => {});
  }, [schemaName]);

  const cargarProductos = useCallback(
    async (reset = false, appendCursor = null) => {
      if (!sede) return;
      const params = new URLSearchParams();
      params.set('sede', sede.codigo);
      params.set('limite', '20');
      params.set('schema_name', schemaName);
      if (sessionId) params.set('session_id', sessionId);
      if (depSeleccionado) {
        const depto = departamentos.find((d) => d.codigo === depSeleccionado);
        if (depto) params.set('departamento', depto.nombre);
      }
      if (grupoSeleccionado != null) params.set('grupo', grupoSeleccionado);
      if (categoriaSeleccionada) params.set('categoria', categoriaSeleccionada);
      if (tagSeleccionado) params.set('tag', tagSeleccionado);
      if (marcaSeleccionada) params.set('marca', marcaSeleccionada);
      if (busqueda) params.set('q', busqueda);
      if (!reset && appendCursor) {
        params.set('ultimo_codigo', appendCursor);
        if (cursorUltimaDescripcionRef.current) params.set('ultima_descripcion', cursorUltimaDescripcionRef.current);
      }

      if (reset) {
        setCargandoProductos(true);
        setProductos([]);
      } else {
        setCargandoMas(true);
      }

      const currentRequestId = ++requestIdRef.current;

      try {
        const res = await axios.get(`${API_URL}/catalogo/productos?${params.toString()}`);
        if (currentRequestId !== requestIdRef.current) return;
        if (res.data.status === 'success') {
          const data = res.data.data || [];
          if (reset) {
            setProductos(data);
          } else {
            setProductos((prev) => [...prev, ...data]);
          }
          setHasMore(res.data.has_more || false);
          setCursorUltimo(res.data.ultimo_codigo || null);
          cursorUltimaDescripcionRef.current = res.data.ultima_descripcion || null;
        }
      } catch {
        /* ignore */
      } finally {
        setCargandoProductos(false);
        setCargandoMas(false);
      }
    },
    [sede, depSeleccionado, grupoSeleccionado, categoriaSeleccionada, tagSeleccionado, marcaSeleccionada, busqueda, departamentos, schemaName, sessionId]
  );

  const onSearchChange = (val) => {
    setBusquedaInput(val);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => setBusqueda(val), 400);
  };

  const seleccionarDepartamento = (codigo) => {
    setDepSeleccionado((prev) => (prev === codigo ? null : codigo));
  };

  const seleccionarGrupo = (codigo) => {
    setGrupoSeleccionado((prev) => (prev === codigo ? null : codigo));
  };

  const seleccionarCategoria = (cat) => {
    setCategoriaSeleccionada((prev) => (prev === cat ? null : cat));
  };

  const seleccionarTag = (tagId) => {
    setTagSeleccionado((prev) => (prev === tagId ? null : tagId));
  };

  const seleccionarMarca = (marca) => {
    setMarcaSeleccionada((prev) => (prev === marca ? null : marca));
  };

  useEffect(() => {
    if (!sede) return;
    const codigo = sede.codigo;
    const sedeCambio = sedeCodigoRef.current !== codigo;
    const filtroCambio =
      filtroAnteriorRef.current.depSeleccionado !== depSeleccionado ||
      filtroAnteriorRef.current.grupoSeleccionado !== grupoSeleccionado ||
      filtroAnteriorRef.current.categoriaSeleccionada !== categoriaSeleccionada ||
      filtroAnteriorRef.current.tagSeleccionado !== tagSeleccionado ||
      filtroAnteriorRef.current.marcaSeleccionada !== marcaSeleccionada ||
      filtroAnteriorRef.current.busqueda !== busqueda;
    if (sedeCambio || filtroCambio) {
      sedeCodigoRef.current = codigo;
      filtroAnteriorRef.current = { depSeleccionado, grupoSeleccionado, categoriaSeleccionada, tagSeleccionado, marcaSeleccionada, busqueda };
      cargarProductos(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sede, depSeleccionado, grupoSeleccionado, categoriaSeleccionada, tagSeleccionado, marcaSeleccionada, busqueda]);

  useEffect(() => {
    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
  }, []);

  return {
    departamentos,
    categorias,
    tags,
    marcas,
    depSeleccionado,
    setDepSeleccionado,
    grupoSeleccionado,
    setGrupoSeleccionado,
    categoriaSeleccionada,
    setCategoriaSeleccionada,
    tagSeleccionado,
    setTagSeleccionado,
    marcaSeleccionada,
    setMarcaSeleccionada,
    busqueda,
    setBusqueda,
    busquedaInput,
    setBusquedaInput,
    productos,
    setProductos,
    cargandoProductos,
    cursorUltimo,
    hasMore,
    cargandoMas,
    cargarProductos,
    onSearchChange,
    seleccionarDepartamento,
    seleccionarGrupo,
    seleccionarCategoria,
    seleccionarTag,
    seleccionarMarca,
    sedeCodigoRef,
    filtroAnteriorRef,
  };
}
